import { App } from 'antd';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { clientConfig, useClient } from 'src/api/axios/config';
import { useAuth } from 'src/contexts/AuthContext';
import { useAppNavigation } from 'src/routes';

import { get, patch, post, put, remove } from '@/api/api';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';

import {
  FanguardApiContractsClientAssetModel,
  FanguardApiContractsClientClientResponseDetailModel,
  FanguardApiContractsClientClientUpdateRequestModel,
  FanguardApiContractsClientConnectionRequestModelMerged,
  FanguardApiContractsClientTestWebhookConnection,
  FanguardCoreManagersCredentialingCredentialArea,
  FanguardCoreManagersCredentialingCredentialingPrinters,
} from '../../api/generated';
import { useMutationWithMessages } from './helpers';

const URI = 'clients';

export const getCredentialingAreasOptions = () => ({
  queryKey: [URI],
  queryFn: async () => {
    const response = await get<FanguardCoreManagersCredentialingCredentialArea[]>({
      uri: `${URI}/credentialing-areas`,
    });
    return response.data;
  },
});

const useGetCredentialingAreas = (): UseQueryResult<FanguardCoreManagersCredentialingCredentialArea[], Error> => {
  return useQuery(getCredentialingAreasOptions());
};

export const getClientOptions = (id: string) => ({
  queryKey: [URI, id],
  queryFn: async () => {
    const response = await get<FanguardApiContractsClientClientResponseDetailModel>({ uri: `${URI}/${id}` });
    return response.data;
  },
});

const useGetClient = (id: string): UseQueryResult<FanguardApiContractsClientClientResponseDetailModel, Error> => {
  return useQuery(getClientOptions(id));
};

export const getClientPrintersOptions = () => ({
  queryKey: [URI, 'printers'],
  queryFn: async () => {
    const response = await get<FanguardCoreManagersCredentialingCredentialingPrinters>({
      uri: `${URI}/printers`,
    });
    return response.data;
  },
});

const useGetClientPrinters = (): UseQueryResult<FanguardCoreManagersCredentialingCredentialingPrinters, Error> => {
  return useQuery(getClientPrintersOptions());
};

const getClientAssetsOptions = (id: string) => ({
  queryKey: [URI, id, 'assets'],
  queryFn: async () => {
    const response = await get<FanguardApiContractsClientAssetModel[]>({ uri: `${URI}/${id}/asset` });
    return response.data;
  },
});

const useGetClientAssets = (id: string): UseQueryResult<FanguardApiContractsClientAssetModel[], Error> => {
  return useQuery(getClientAssetsOptions(id));
};

const useGetClientConnection = (id: string) => {
  return useQuery({
    queryKey: [URI, clientConfig.getClient(), id],
    queryFn: async () => {
      const response = await get<FanguardApiContractsClientConnectionRequestModelMerged>({
        uri: `${URI}/${clientConfig.getClient()}/connection/${id}`,
      });
      return response.data;
    },
    enabled: !!id,
  });
};

const useCreateClientConnection = () => {
  const { navigateTo } = useAppNavigation();
  const queryClient = useQueryClient();
  const client = clientConfig.getClient();

  return useMutationWithMessages<
    AxiosResponse<FanguardApiContractsClientConnectionRequestModelMerged>,
    FanguardApiContractsClientConnectionRequestModelMerged
  >((data) => post({ uri: `${URI}/${client}/connection`, data }), {
    action: 'create',
    entityType: 'connection',
    queryKeysToInvalidate: [URI, client],
    onSuccess: async (d) => {
      const connectionId = d.data.id;
      if (connectionId) {
        await queryClient.invalidateQueries({ queryKey: [URI, client, connectionId] });
        await queryClient.refetchQueries({ queryKey: [URI, client, connectionId] });
      }
      navigateTo.connectionsDetails(connectionId ?? '');
    },
  });
};

const useUpdateClientConnection = (id: string) => {
  return useMutationWithMessages<
    AxiosResponse<FanguardApiContractsClientConnectionRequestModelMerged>,
    Error,
    FanguardApiContractsClientConnectionRequestModelMerged
  >((data) => patch({ uri: `${URI}/${clientConfig.getClient()}/connection/${data.id}`, data }), {
    action: 'update',
    entityType: 'connection',
    queryKeysToInvalidate: [URI, clientConfig.getClient(), id],
  });
};

const useDeleteClientConnection = () => {
  const { navigateTo } = useAppNavigation();

  return useMutationWithMessages(
    (id: string) => remove({ uri: `${URI}/${clientConfig.getClient()}/connection/${id}` }),
    {
      action: 'delete',
      entityType: 'connection',
      queryKeysToInvalidate: [clientConfig.getClient()],
      onSuccess: () => {
        navigateTo.connections();
      },
    },
  );
};

const useUpdateClient = () => {
  const client = useClient();
  const { username } = useAuth();

  return useMutationWithMessages(
    (data: FanguardApiContractsClientClientUpdateRequestModel) => patch({ uri: `${URI}/${client}`, data }),
    {
      action: 'update',
      entityType: 'client',
      queryKeysToInvalidate: [URI, client, username ?? ''],
    },
  );
};

const useUpdateClientAssets = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const client = useClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FanguardApiContractsClientAssetModel) => {
      const response = await post({ uri: `${URI}/${client}/asset`, data });
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [URI, client, 'assets'] });
      message.success(t('assets.uploadSuccess'));
    },
    onError: (error) => {
      const errorMessage = error?.message || t('assets.uploadError');

      if (errorMessage.toLowerCase().includes('duplicate') || errorMessage.toLowerCase().includes('already exists')) {
        message.error(t('assets.duplicateError'));
      } else {
        message.error(errorMessage);
      }
      queryClient.invalidateQueries({ queryKey: [URI, client, 'assets'] });
    },
  });
};

const useDeleteClientAsset = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const client = useClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return remove({ uri: `${URI}/${client}/asset`, data: { id } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [URI, client, 'assets'] });
      message.success(t('assets.deleteAssetSuccess'));
    },
    onError: () => {
      message.error(t('assets.deleteAssetError'));
    },
  });
};

const useGetClientConnections = () => {
  return useQuery({
    queryKey: [URI, clientConfig.getClient(), 'connections'],
    queryFn: async () => {
      const response = await get<FanguardApiContractsClientConnectionRequestModelMerged[]>({
        uri: `${URI}/${clientConfig.getClient()}/connections`,
      });
      return response.data;
    },
  });
};

const useTestWebhook = () => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (data: FanguardApiContractsClientTestWebhookConnection) => {
      const response = await post({ uri: `${URI}/test-webhook`, data });
      return response.data;
    },
    onSuccess: () => {
      message.success(t('connections.connectionAdd.general.notification.testWebhookSuccess'));
    },
    onError: () => {
      message.error(t('connections.connectionAdd.general.notification.testWebhookError'));
    },
  });
};

const useGetReportCategories = () => {
  const client = useClient();
  return useQuery({
    queryKey: [URI, client, 'report-categories'],
    queryFn: async () => {
      const response = await get<string[]>({ uri: `${URI}/${client}/report-categories` });

      return response.data;
    },
  });
};

const useCreateConnectionServiceKey = () => {
  const queryClient = useQueryClient();
  const client = clientConfig.getClient();

  return useMutation({
    mutationFn: async (connectionId: string) => {
      if (!connectionId) {
        throw new Error('Connection ID is required');
      }
      if (!client) {
        throw new Error('Client ID is required');
      }
      const uri = `${URI}/${client}/connection/${connectionId}/service-keys`;

      const response = await post({
        uri,
      });
      return response.data;
    },
    onSuccess: async (_, connectionId) => {
      await queryClient.invalidateQueries({ queryKey: [URI, client, connectionId] });
      await queryClient.refetchQueries({ queryKey: [URI, client, connectionId] });
      queryClient.invalidateQueries({ queryKey: [URI, client, 'connections'] });
    },
  });
};

const useDeleteConnectionServiceKey = () => {
  const queryClient = useQueryClient();
  const client = clientConfig.getClient();

  return useMutation({
    mutationFn: async ({ connectionId, serviceKeyId }: { connectionId: string; serviceKeyId: string }) => {
      await remove({ uri: `${URI}/${client}/connection/${connectionId}/service-keys/${serviceKeyId}` });
    },
    onSuccess: (_, { connectionId }) => {
      queryClient.invalidateQueries({ queryKey: [URI, client, connectionId] });
      queryClient.invalidateQueries({ queryKey: [URI, client, 'connections'] });
    },
  });
};

const useSetActiveConnectionServiceKey = () => {
  const queryClient = useQueryClient();
  const client = clientConfig.getClient();

  return useMutation({
    mutationFn: async ({
      connectionId,
      serviceKeyId,
      isActive,
    }: {
      connectionId: string;
      serviceKeyId: string;
      isActive: boolean;
    }) => {
      await put({
        uri: `${URI}/${client}/connection/${connectionId}/service-keys/${serviceKeyId}?setActive=${isActive}`,
      });
    },
    onSuccess: async (_, { connectionId }) => {
      await queryClient.invalidateQueries({ queryKey: [URI, client, connectionId] });
      await queryClient.refetchQueries({ queryKey: [URI, client, connectionId] });
      queryClient.invalidateQueries({ queryKey: [URI, client, 'connections'] });
    },
  });
};

export {
  useCreateClientConnection,
  useCreateConnectionServiceKey,
  useDeleteClientAsset,
  useDeleteClientConnection,
  useDeleteConnectionServiceKey,
  useGetClient,
  useGetClientAssets,
  useGetClientConnection,
  useGetClientConnections,
  useGetClientPrinters,
  useGetCredentialingAreas,
  useGetReportCategories,
  useSetActiveConnectionServiceKey,
  useTestWebhook,
  useUpdateClient,
  useUpdateClientAssets,
  useUpdateClientConnection,
};
